import { findCurrentSlide } from './utils/find-current-slide';
import { filterQuestions } from './utils/filter-questions';
import {
  getPreviousWizardStep,
  isFirstStep,
  getNextWizardStep,
  isLastStep
} from './utils/wizard-step-utils';
import { WizardState, WizardAction, WizardActionType } from './utils/wizard-types';
import { SectionKey } from 'types';

export const wizardReducer = (
  state: WizardState,
  action: WizardAction,
  sectionData: PvWizardSections,
  sectionOrder: SectionKey[]
) => {
  const currentStepQuestions = filterQuestions(
    sectionData[state.activeWizardStep].questions,
    state.answers
  );

  switch (action.type) {
    case WizardActionType.PREV_QUESTION:
      // if is first question of section go to prev section and select the last slide
      if (state.activeSlide === 0) {
        const previousWizardStep = getPreviousWizardStep(state.activeWizardStep, sectionOrder);
        const previousWizardStepQuestions = filterQuestions(
          sectionData[previousWizardStep].questions,
          state.answers
        );
        const previousWizardStepQuestionsLastIndex = previousWizardStepQuestions.length - 1;

        return {
          ...state,
          activeWizardStep: previousWizardStep,
          activeSlide: previousWizardStepQuestionsLastIndex,
          activeSlideKey: previousWizardStepQuestions[previousWizardStepQuestionsLastIndex].key,
          isLastQuestion: true,
          isLastStep: false,
          isFirstStep: isFirstStep(previousWizardStep, sectionOrder)
        };
      }

      const previousQuestionIndex = Math.max(state.activeSlide - 1, 0);

      return {
        ...state,
        activeSlide: previousQuestionIndex,
        activeSlideKey: currentStepQuestions[previousQuestionIndex].key,
        isLastQuestion: false,
        isLastStep: state.isLastStep,
        isFirstStep: state.isFirstStep
      };

    case WizardActionType.NEXT_QUESTION:
      // if is last question of section go to next section & set active slide to 0
      if (state.isLastQuestion) {
        const nextWizardStep = getNextWizardStep(state.activeWizardStep, sectionOrder);
        const isNextStepLastStep = isLastStep(nextWizardStep, sectionOrder);

        return {
          ...state,
          activeWizardStep: nextWizardStep,
          activeSlide: 0,
          activeSlideKey: sectionData[nextWizardStep]?.questions?.[0]?.key || nextWizardStep,
          // next step has only one question so it's the last question
          isLastQuestion: sectionData[nextWizardStep].questions.length === 1,
          isLastStep: isNextStepLastStep,
          isFirstStep: false
        };
      }

      // otherwise increase active slide index
      const nextSlideIndex = Math.min(state.activeSlide + 1, currentStepQuestions.length - 1);

      return {
        ...state,
        activeSlide: nextSlideIndex,
        activeSlideKey: currentStepQuestions[nextSlideIndex].key,
        isLastQuestion: nextSlideIndex === currentStepQuestions.length - 1,
        isLastStep: state.isLastStep,
        isFirstStep: state.isFirstStep
      };

    case WizardActionType.UPDATE_ANSWER:
      const updatedAnswers = { ...state.answers, ...action.payload?.updatedAnswer };
      const updatedCurrentStepQuestions = filterQuestions(
        sectionData[state.activeWizardStep].questions,
        updatedAnswers
      );

      return {
        ...state,
        isLastQuestion: state.activeSlide === updatedCurrentStepQuestions.length - 1,
        answers: updatedAnswers
      };
    case WizardActionType.RESET_WIZARD:
      const resetedAnswers = {};
      const resetedWizardStep = sectionOrder[0];
      const resetedStepQuestions = filterQuestions(
        sectionData[resetedWizardStep].questions,
        resetedAnswers
      );

      return {
        isLastQuestion: false,
        isLastStep: false,
        isFirstStep: true,
        answers: resetedAnswers,
        activeWizardStep: resetedWizardStep,
        activeSlideKey: resetedStepQuestions[0].key,
        activeSlide: 0,
        isWizardClickedThrough: false
      };

    case WizardActionType.SET_FORM_DATA:
      return {
        ...state,
        activeSlide: 1,
        activeSlideKey: sectionData.solarStorage.questions[1].key,
        isLastQuestion: true,
        partnerOfferingForm: action.payload?.partnerOfferingForm
      };

    // used for browser back to restore previous wizard state
    case WizardActionType.RESTORE_ANSWERS:
      const restoredAnswers = action.payload?.updatedAnswer ?? {};

      const { initialSectionOrderIndex, initialSlideKey, initialActiveSlideIndex } =
        findCurrentSlide(restoredAnswers, sectionOrder, sectionData);
      const updatedActiveWizardStep = sectionOrder[initialSectionOrderIndex];
      const initialActiveSlide = initialSlideKey;

      const isLastQuestion = initialSlideKey === 'roofDetails';
      const isLastWizardStep = updatedActiveWizardStep === 'solarStorage';

      const isWizardClickedThrough =
        initialActiveSlideIndex < 0 && typeof initialActiveSlide === 'number';

      const updatedState = {
        isLastQuestion: isLastQuestion || isWizardClickedThrough,
        isLastStep: isLastWizardStep,
        isFirstStep: Boolean(
          initialActiveSlide === 'houseType' && !Object.keys(restoredAnswers).length
        ),
        answers: restoredAnswers,
        activeSlide: initialActiveSlideIndex,
        activeWizardStep: updatedActiveWizardStep,
        activeSlideKey: initialActiveSlide,
        isWizardClickedThrough: Boolean(
          initialActiveSlideIndex < 0 && typeof initialActiveSlide === 'number'
        )
      };

      return updatedState;
    default:
      throw new Error();
  }
};
